<template>
  <div v-if="workspaceLayout" id="app" class="h-screen">
    <ConfirmationAlert v-if="confirmationOpened" />
    <ModalVue v-if="modalOpened" />
    <NotificationsOverlay />
    <WorkspaceRoot v-if="onWorkspace">
      <RouterView v-if="routeRenders" id="body-container" class="h-full" />
    </WorkspaceRoot>
    <RouterView v-else-if="routeRenders" id="body-container" class="h-full" />
    <!-- whoami failed transiently while a session is held (th-10zrs): the
         bearer is kept and the check retries, so the member sees this rather
         than a guest workspace over a session nobody ended. A public page has
         already rendered above, so it never shows there. It sits outside
         WorkspaceRoot, so a workspace chunk the deploy cannot serve never
         hides it. -->
    <div
      v-if="!routeRenders && authStore.identityInterrupted"
      class="flex h-full items-center justify-center p-6"
      data-test="identity-interrupted"
    >
      <div role="status" aria-live="polite" class="max-w-sm text-center">
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
          Tower Hunt can't reach its server right now.
        </p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          You're still signed in.
          <template v-if="authStore.identityStatus === 'retrying'">Reconnecting…</template>
        </p>
        <button
          v-if="authStore.identityStatus === 'unreachable'"
          type="button"
          class="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="authStore.retryIdentityCheck()"
        >
          Try again
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
// THE THIN BOOT SHELL (launch-marketing-pages design M2; spec
// `public-marketing-pages` § "Public routes boot through a thin shell"). It
// keeps what every route uses: the router view, the window measurement, the
// theme, the three app-wide overlays, the push subscription's hooks and the
// identity check. Everything the workspace needs at the root lives in
// `WorkspaceRoot`, loaded only on a route that renders the workspace, so the
// shell imports no workspace store or component.
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { useWorkspaceLayoutStore } from "@/stores/workspaceLayout";
import { useLocalStorageCacheStore } from "@/stores/localStorageCache";
import { usePushSubscription } from "@/composables/usePushSubscription";
import { startGuestToken, stopGuestToken } from "@/services/guestToken";
import { computed, defineAsyncComponent, onMounted, onUnmounted, ref, watch, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { isWorkspaceRoute } from "@/utils/workspaceRoute";
import { useModalStore } from "@/stores/modal";
import NotificationsOverlay from "@/components/main-layout/NotificationsOverlay.vue";

const WorkspaceRoot = defineAsyncComponent(
  () => import("@/components/main-layout/WorkspaceRoot.vue"),
);

// THE TWO DIALOGS LOAD WHEN ONE FIRST OPENS (th-n1u7mq). A public page opens
// neither until a visitor acts, so the modal and the confirmation, with the
// dialog machinery they carry, are chunks of their own: each mounts the first
// time its store slot fills (it enters on its own `appear` transition) and
// stays mounted, so every later open and close animates as before. The toast
// live region stays mounted from the start: an `aria-live` region is announced
// only when it is in the page before its content arrives.
const ConfirmationAlert = defineAsyncComponent(
  () => import("@/components/main-layout/ConfirmationAlert.vue"),
);
const ModalVue = defineAsyncComponent(
  () => import("@/components/main-layout/ModalVue.vue"),
);
const modalStore = useModalStore();
const { size: modalSize, confirmationMessage } = storeToRefs(modalStore);
const modalOpened = ref(false);
const confirmationOpened = ref(false);
watch(
  () => Boolean(modalSize.value),
  (open) => {
    if (open) modalOpened.value = true;
  },
  { immediate: true },
);
watch(
  () => Boolean(confirmationMessage.value),
  (open) => {
    if (open) confirmationOpened.value = true;
  },
  { immediate: true },
);

const authStore = useAuthStore();
const userStore = useUserStore();
const { signedIn } = storeToRefs(userStore);
const layoutStore = useWorkspaceLayoutStore();
const { windowWidth, windowHeight, workspaceLayout, isPortrait, isDesktop } =
  storeToRefs(layoutStore);

// Instantiated here, so its `authTransition` clear of the user-scoped caches is
// live before the identity check below can dispatch one, whichever store
// happens to read the cache first.
useLocalStorageCacheStore();

// Register the push Service Worker once the user is signed in (post-login).
// The composable is a no-op unless WEB_PUSH_ENABLED is on and the browser has
// already granted permission — it never prompts here (the opt-in prompt lives
// in Account Settings). Guests (signedIn === false) trigger nothing.
const { initializePostLogin, registerAuthTransitionRevocation } =
  usePushSubscription();
watch(
  signedIn,
  (isSignedIn) => {
    if (isSignedIn) {
      initializePostLogin().catch(() => {
        // Best-effort background registration; a failure here must never break
        // app boot. The opt-in flow surfaces its own errors on user action.
      });
    }
  },
  { immediate: true },
);

// Revoke this device's push subscription when the auth store dispatches an
// `authTransition` for sign-out / account-switch, so a shared browser never
// leaks the prior user's pushes (design D9). Registered in onMounted below —
// before getCurrentUser — and torn down in onUnmounted.
let removeAuthTransitionRevocation = null;

// Measured once the window has been still for 250 ms (a trailing debounce).
let windowSizeTimer = null;
function setWindowSize() {
  clearTimeout(windowSizeTimer);
  windowSizeTimer = setTimeout(() => {
    windowWidth.value = window.innerWidth;
    windowHeight.value = window.innerHeight;
  }, 250);
}

onMounted(async () => {
  // Live before getCurrentUser so it catches any transition the identity check
  // triggers; the composable's revocation is fire-and-forget internally.
  removeAuthTransitionRevocation = registerAuthTransitionRevocation();

  authStore.getCurrentUser();
  setWindowSize();

  // The invisible guest token (th-mpimer.26): one passive listener mints it after a
  // guest's first genuine interaction, on every route, public pages included.
  startGuestToken();

  await nextTick();
  window.addEventListener("resize", setWindowSize);
  layoutStore.matchLayoutToScreen();

  // Initialize app theme (for UI chrome + map style)
  layoutStore.initAppTheme();
});

// A light-only surface declares `meta.surfaceTheme: 'light'` on its route
// (nested records merge into `route.meta`); the store pins the resolved theme
// while it is mounted, so the document backdrop and every component on the
// page resolve one theme together (th-989tg).
const route = useRoute();
watch(
  () => route.meta.surfaceTheme,
  (theme) => layoutStore.setSurfaceTheme(theme),
  { immediate: true }
);

// The workspace wraps the router view only where the route renders it
// (`isWorkspaceRoute`, the one structural test), so a public page never loads
// its chunk.
const onWorkspace = computed(() => isWorkspaceRoute(route));

// A public page (`meta.public`) renders without waiting on whoami, so Home
// paints at once and a deploy's transient whoami failure never hides it. Every
// other route waits for the identity check.
const routeRenders = computed(() => authStore.identityChecked || Boolean(route.meta.public));

watch(isPortrait, () => {
  layoutStore.matchLayoutToScreen();
});
// The shell's side of a layout crossing; `WorkspaceRoot` holds the workspace's.
watch(isDesktop, () => {
  if (isDesktop.value) layoutStore.matchLayoutToScreen();
});

onUnmounted(() => {
  window.removeEventListener("resize", setWindowSize);
  if (removeAuthTransitionRevocation) removeAuthTransitionRevocation();
  stopGuestToken();
});
</script>

<style>
.dont-break-out {
  /* These are technically the same, but use both */
  overflow-wrap: break-word;
  word-wrap: break-word;

  -ms-word-break: break-all;
  /* This is the dangerous one in WebKit, as it breaks things wherever */
  word-break: break-all;
  /* Instead use this non-standard one: */
  word-break: break-word;

  /* Adds a hyphen where the word breaks, if supported (No Blink) */
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  -webkit-hyphens: auto;
  hyphens: auto;
}

.filtered-marker-icon {
  z-index: 1020 !important;
}

.dimmed-marker-icon {
  z-index: 1000 !important;
}
</style>
